import React, { useState, useRef, useEffect } from 'react';
import { Lightbulb } from 'lucide-react';
import './RegenerateModal.css';

interface RegenerateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRegenerate: (additionalContext?: string) => void;
  originalPrompt: string;
  isLoading?: boolean;
}

const RegenerateModal = React.memo<RegenerateModalProps>(({
  isOpen,
  onClose,
  onRegenerate,
  originalPrompt,
  isLoading = false
}) => {
  const [additionalContext, setAdditionalContext] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  // Focus textarea when modal opens
  useEffect(() => {
    if (isOpen && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Handle click outside modal
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, onClose]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onRegenerate(additionalContext.trim() || undefined);
    setAdditionalContext('');
  };

  const handleQuickRegenerate = () => {
    onRegenerate();
    setAdditionalContext('');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setAdditionalContext(e.target.value);
    
    // Auto-resize textarea
    const textarea = e.target;
    textarea.style.height = 'auto';
    textarea.style.height = Math.min(textarea.scrollHeight, 120) + 'px';
  };

  if (!isOpen) return null;

  return (
    <div className="regenerate-modal-overlay">
      <div className="regenerate-modal" ref={modalRef}>
        <div className="regenerate-modal-header">
          <h3 className="regenerate-modal-title">
            <span className="regenerate-modal-icon">🔄</span>
            Regenerate Response
          </h3>
          <button 
            className="regenerate-modal-close" 
            onClick={onClose}
            disabled={isLoading}
          >
            ×
          </button>
        </div>

        <div className="regenerate-modal-content">
          <div className="original-prompt-section">
            <label className="original-prompt-label">Original Message:</label>
            <div className="original-prompt-display">
              {originalPrompt}
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="additional-context-section">
              <label className="additional-context-label" htmlFor="additional-context">
                Additional Instructions <span className="optional-text">(optional)</span>:
              </label>
              <textarea
                id="additional-context"
                ref={textareaRef}
                className="additional-context-input"
                placeholder="e.g., Filter for only California documents, Present in bullet points, Be more specific..."
                value={additionalContext}
                onChange={handleInputChange}
                disabled={isLoading}
                rows={3}
              />
              <div className="context-hints">
                <span className="hint">
                  <Lightbulb className="h-4 w-4 inline mr-1" />
                  Examples:
                </span>
                <button
                  type="button"
                  className="hint-button"
                  onClick={() => setAdditionalContext('Filter for only California documents')}
                  disabled={isLoading}
                >
                  Filter for California only
                </button>
                <button
                  type="button"
                  className="hint-button"
                  onClick={() => setAdditionalContext('Present the information in bullet points')}
                  disabled={isLoading}
                >
                  Use bullet points
                </button>
                <button
                  type="button"
                  className="hint-button"
                  onClick={() => setAdditionalContext('Be more specific and detailed')}
                  disabled={isLoading}
                >
                  More detailed
                </button>
              </div>
            </div>

            <div className="regenerate-modal-actions">
              <button
                type="button"
                className="regenerate-modal-btn secondary"
                onClick={handleQuickRegenerate}
                disabled={isLoading}
              >
                <span className="btn-icon">🔄</span>
                Quick Regenerate
              </button>
              <button
                type="button"
                className="regenerate-modal-btn tertiary"
                onClick={onClose}
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="regenerate-modal-btn primary"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <span className="loading-spinner"></span>
                    Regenerating...
                  </>
                ) : (
                  <>
                    <span className="btn-icon">✨</span>
                    Regenerate with Context
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
});

export default RegenerateModal; 